"use client";

import * as React from "react";

import { Link, usePathname } from "@/i18n/navigation";
import { cn } from "@/lib/utils";

import { NAV_LINKS } from "./data";

interface VsNavLink {
  readonly href: string;
  readonly label: string;
}

interface VsNavProps {
  /** Threshold (px from top) at which the nav becomes solid + fixed. */
  stickyThreshold?: number;
  className?: string;
  applyHref?: string;
  applyLabel?: string;
  /** Override nav link list. Defaults to the two top-level pages
   *  (Visionaries Summit + Pitch Night). */
  navLinks?: readonly VsNavLink[];
}

/**
 * Visionaries Summit page nav. Sits below the action bar (offset by
 * `--vs-action-h`); is transparent at the top of the page and snaps to a
 * dark blurred bar the moment the user scrolls.
 *
 * Layout:
 * - Desktop: one row — logo, inline nav links (centered area), Apply CTA.
 * - Mobile: two rows — top row has logo + Apply CTA, bottom row has the
 *   nav links inline (matching the desktop link treatment, just stacked
 *   onto its own line so the CTA can stay full-size).
 */
export function VsNav({
  stickyThreshold = 8,
  className,
  applyHref = "#apply",
  applyLabel = "Apply to attend",
  navLinks = NAV_LINKS,
}: VsNavProps) {
  const navRef = React.useRef<HTMLDivElement>(null);
  const [isFixed, setIsFixed] = React.useState(false);
  const frameRef = React.useRef<number | null>(null);
  const pathname = usePathname();

  React.useEffect(() => {
    const compute = () => {
      frameRef.current = null;
      setIsFixed(window.scrollY > stickyThreshold);
    };

    const schedule = () => {
      if (frameRef.current !== null) return;
      frameRef.current = window.requestAnimationFrame(compute);
    };

    compute();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    return () => {
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      if (frameRef.current !== null) {
        window.cancelAnimationFrame(frameRef.current);
      }
    };
  }, [stickyThreshold]);

  // Active = exact pathname match (handles trailing slash + locale prefix
  // automatically because next-intl's usePathname strips the locale).
  const isActive = (href: string) => {
    if (href.startsWith("#")) return false;
    if (href === "/visionaries-summit")
      return pathname === "/visionaries-summit";
    return pathname === href || pathname.startsWith(`${href}/`);
  };

  return (
    <div
      ref={navRef}
      data-vs-page-nav
      className={cn(
        "fixed left-0 z-50 w-full transition-[background-color,backdrop-filter] duration-200 ease-out motion-reduce:transition-none",
        isFixed
          ? "bg-[rgba(13,13,13,0.92)] backdrop-blur-md"
          : "bg-transparent",
        className
      )}
      style={{ top: "var(--vs-action-h, 36px)" }}
    >
      {/* Row 1 — logo + Apply CTA. On md+ the inline nav links sit between
          them and the row gets a bottom border; on mobile the links
          move to the second row below and the border between the two
          mobile rows is dropped (the second row carries the border). */}
      <div className="flex h-[60px] items-center justify-center px-6 md:border-b md:border-white/40 md:px-12">
        <div className="flex w-full max-w-[1280px] items-center justify-between gap-4">
          <Link
            href="/"
            aria-label="Wayflyer home"
            translate="no"
            className={cn(
              "flex h-6 flex-shrink-0 items-center text-white",
              "rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent"
            )}
          >
            <svg
              viewBox="0 0 147 32"
              fill="currentColor"
              aria-hidden
              className="h-[22px] w-auto"
            >
              <path d="M145.492 6.91591C144.205 6.91591 143.052 7.22723 142.032 7.82765C141.012 8.45031 140.258 9.36205 139.792 10.6074H138.55V7.11605H133.582V11.9861H135.999V24.9062H140.812V15.9222C140.812 14.5879 141.167 13.5872 141.877 12.9646C142.586 12.3197 143.584 11.9861 144.871 11.9861H147V6.91591H145.492Z" />
              <path d="M127.927 7.82766C126.596 7.11606 125.021 6.76025 123.158 6.76025C121.295 6.76025 119.61 7.13829 118.212 7.89437C116.837 8.62822 115.773 9.67339 115.019 11.0744C114.265 12.4309 113.887 14.0764 113.887 15.9666C113.887 17.8568 114.265 19.5247 115.041 20.9256C115.817 22.3044 116.904 23.394 118.323 24.1501C119.743 24.9062 121.406 25.2842 123.313 25.2842C125.221 25.2842 127.594 24.7505 129.102 23.6831C130.61 22.5935 131.586 21.1035 131.963 19.2133H127.062C126.795 19.9472 126.374 20.4809 125.753 20.8367C125.132 21.1925 124.311 21.3704 123.269 21.3704C122.227 21.3704 120.874 21.0146 120.075 20.3252C119.277 19.6359 118.767 18.6352 118.567 17.3009H132.03V15.6331C132.03 13.7874 131.675 12.2085 130.965 10.8965C130.255 9.5622 129.235 8.56151 127.882 7.8499L127.927 7.82766ZM118.745 14.032C119.011 12.9201 119.499 12.0751 120.253 11.5191C121.029 10.9409 122.005 10.6518 123.203 10.6518C124.4 10.6518 125.354 10.9409 126.064 11.5191C126.773 12.0751 127.217 12.9201 127.372 14.032H118.745Z" />
              <path d="M76.8268 0.978457C76.1171 1.60111 75.7623 2.60181 75.7623 3.93607V7.11605H72.5464V10.9632H75.7179V24.9062H80.6194V10.9632H84.545V7.11605H80.5529V3.84712H84.8999V0H79.821C78.5346 0 77.5366 0.333565 76.8268 0.978457Z" />
              <path d="M87.1176 0V20.9701C87.1176 22.3044 87.4724 23.3051 88.1822 23.9277C88.8919 24.5726 89.8899 24.9062 91.1763 24.9062H94.1926V21.0591H92.0412V0H87.1176Z" />
              <path d="M61.4128 19.1022H61.3019L56.3338 7.11606H51.1218L58.7735 24.7505L57.3762 28.0639H53.4728V32H57.5315C58.8622 32 59.8602 31.6664 60.5256 31.0215C61.1688 30.3989 61.7454 29.4871 62.2334 28.3308L63.675 24.8839L71.2823 7.09382H66.2477L61.3906 19.0799L61.4128 19.1022Z" />
              <path d="M49.2588 13.7206C49.2588 11.4524 48.5491 9.71786 47.1519 8.53927C45.7768 7.36067 43.7585 6.76025 41.1193 6.76025C38.48 6.76025 36.8166 7.27172 35.375 8.27241C33.9334 9.27311 33.024 10.763 32.6248 12.7199H37.5041C37.9255 11.3412 39.1232 10.6518 41.1193 10.6518C43.1153 10.6518 44.557 11.6081 44.557 13.5205V14.2099H39.478C37.1936 14.2099 35.4415 14.6769 34.2217 15.6331C33.0019 16.5893 32.3809 17.9458 32.3809 19.7026C32.3809 21.4594 32.9797 22.7047 34.1552 23.7276C35.3306 24.7283 36.9719 25.2398 39.0566 25.2398C41.1414 25.2398 41.8733 24.9729 42.8936 24.417C43.9138 23.8388 44.7344 23.0382 45.3554 22.0153H46.5974V24.9284H51.5654V20.0584H49.2588V13.7206ZM44.5791 17.7234C44.5791 18.435 44.3795 19.0799 43.9581 19.6581C43.5367 20.214 42.9823 20.6588 42.2726 20.9924C41.5628 21.3259 40.7422 21.5038 39.8107 21.5038C38.8792 21.5038 38.3026 21.3259 37.7925 20.9701C37.3045 20.6143 37.0606 20.1029 37.0606 19.458C37.0606 18.1237 37.9477 17.4788 39.722 17.4788H44.5791V17.7457V17.7234Z" />
              <path d="M22.6666 0V4.84781H27.0802V4.98124L23.8864 17.0785H23.7755L19.5172 3.84712H10.4905V8.71716H11.7325V8.87283L9.22633 17.0785H9.07108L6.43182 7.11605H0V11.9861H2.7058V12.1195L6.32092 24.9062H11.3333L16.3013 11.1188H16.4122L21.6242 24.9062H26.6144L33.7116 0H22.6666Z" />
              <path d="M105.593 11.9861H106.835V12.1418L104.018 19.1022H103.907L98.939 7.11606H93.727L101.379 24.7505L99.9814 28.0639H96.0779V32H100.137C101.467 32 102.465 31.6664 103.131 31.0215C103.774 30.3989 104.351 29.4871 104.839 28.3308L106.28 24.8839L113.887 7.09382H105.593V11.9861Z" />
            </svg>
          </Link>
          <nav
            aria-label="Page sections"
            className="hidden items-center gap-1 md:flex"
          >
            {navLinks.map((link) => {
              const active = isActive(link.href);
              return (
                <Link
                  key={link.href}
                  href={link.href}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "relative px-2.5 py-2 text-[14px] font-normal tracking-[-0.02em] text-white transition-opacity motion-reduce:transition-none",
                    "rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent",
                    active ? "opacity-100" : "opacity-80 hover:opacity-100"
                  )}
                >
                  {link.label}
                  {active && (
                    <span
                      aria-hidden
                      className="absolute inset-x-2.5 -bottom-[11px] h-[1px] bg-white"
                    />
                  )}
                </Link>
              );
            })}
          </nav>
          <a
            href={applyHref}
            target={applyHref.startsWith("#") ? undefined : "_blank"}
            rel={applyHref.startsWith("#") ? undefined : "noopener noreferrer"}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full bg-white px-4 py-2 text-[14px] leading-none font-medium whitespace-nowrap text-[#0d0d0d] transition-colors hover:bg-white/90 motion-reduce:transition-none",
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0d0d0d]"
            )}
          >
            {applyLabel}
            <span aria-hidden>→</span>
          </a>
        </div>
      </div>

      {/* Row 2 (mobile only) — nav links rendered inline like the desktop
          treatment, just stacked onto their own line so the CTA stays at
          full size on the row above. */}
      <nav
        aria-label="Page sections"
        className="flex h-[48px] items-center justify-center gap-2 border-b border-white/40 px-6 md:hidden"
      >
        {navLinks.map((link) => {
          const active = isActive(link.href);
          return (
            <Link
              key={link.href}
              href={link.href}
              aria-current={active ? "page" : undefined}
              className={cn(
                "relative px-2.5 py-2 text-[14px] font-normal tracking-[-0.02em] text-white transition-opacity motion-reduce:transition-none",
                "rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 focus-visible:ring-offset-1 focus-visible:ring-offset-transparent",
                active ? "opacity-100" : "opacity-80 hover:opacity-100"
              )}
            >
              {link.label}
              {active && (
                <span
                  aria-hidden
                  className="absolute inset-x-2.5 -bottom-[5px] h-[1px] bg-white"
                />
              )}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
